import callApi, { CallApiResult, RequestHeaders } from 'src/api/index';
import EntityDescription, { getProjectionParam, Projection } from 'src/store/entities/types/EntityDescription';

/**
 * Fetch all items for the given entity.
 * If the projection is present, it will be used to get the desired shape of the entity
 */
export const fetchAll = (
    entityDescription: EntityDescription,
    projection: Projection = Projection.NONE,
    headers?: RequestHeaders
): Promise<CallApiResult> => {
    const endpoint = `/api/${entityDescription.repository}`;
    const params = getProjectionParam(projection);

    return callApi(endpoint, { params, headers });
};

/**
 * Fetch a single item of an entity by the given id.
 * If the projection is present, it will be used to get the desired shape of the entity
 */
export const fetchById = (
    entityDescription: EntityDescription,
    id: string,
    projection: Projection = Projection.NONE,
    headers?: RequestHeaders
): Promise<CallApiResult> => {
    const endpoint = `/api/${entityDescription.repository}/${id}`;
    const params = getProjectionParam(projection);

    return callApi(endpoint, { params, headers });
};

/**
 * Fetch entities by the given searchMethod
 * If the projection is present, it will be used to get the desired shape of the entity
 */
export function search(
    entityDescription: EntityDescription,
    searchMethod: string,
    projection: Projection = Projection.NONE,
    headers?: RequestHeaders
): Promise<CallApiResult> {
    const endpoint = `/api/${entityDescription.repository}${searchMethod}`;
    const params = getProjectionParam(projection);

    return callApi(endpoint, { params, headers });
}

/**
 * Creates a new entity with the given data.
 * If the projection is present, it will be used to get the desired shape of the new crated entity in the response.
 */
export const create = (
    entityDescription: EntityDescription,
    data: any,
    projection: Projection = Projection.NONE,
    headers?: RequestHeaders
): Promise<CallApiResult> => {
    const endpoint = `/api/${entityDescription.repository}`;
    return callApi(endpoint, { method: 'POST', data, headers });
};

/**
 * Batch creates entities of the given description
 * The response will contain all created items
 */
export const createBatch = (
    entityDescription: EntityDescription,
    data: any[],
    projection: Projection = Projection.NONE,
    headers?: RequestHeaders
): Promise<CallApiResult> => {
    const endpoint = `/api/${entityDescription.repository}/batch`;

    return callApi(endpoint, { method: 'POST', headers, data });
};

/**
 * Patches an existing entity with the given data.
 * If the projection is present, it will be used to get the desired shape of the patched entity in the response.
 */
export const patch = (
    entityDescription: EntityDescription,
    data: any,
    projection: Projection = Projection.NONE,
    headers?: RequestHeaders,
    patchMethod?: string
): Promise<CallApiResult> => {
    let endpoint = `/api/${entityDescription.repository}/${data.id}`;
    if (patchMethod != null) endpoint += `/${patchMethod}`;

    return callApi(endpoint, { method: 'PATCH', data, headers });
};

/**
 * Deletes an entity by id
 */
export const deleteById = (
    entityDescription: EntityDescription,
    id: number,
    headers?: RequestHeaders
): Promise<CallApiResult> => {
    const endpoint = `/api/${entityDescription.repository}/${id}`;

    return callApi(endpoint, { method: 'DELETE', headers });
};
